import React, { useEffect, useState } from 'react'
import { Button, Card, Modal, notification } from 'antd'
import idx from 'idx'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Route } from 'react-router-dom'
import { fetchRedirectById, fetchRedirectsList, fetchAllRedirects, removeRedirectById, resetUpdateRedirectStatus, updateRedirect } from '~actions'
import { RedirectCard, ResultModal } from '~components'
import { getCurrentUser } from '~context'
import { PageLayout, Spinner } from '~stories'

import styles from './EditRedirect.module.css'

const NOTIFICATION_MESSAGES = {
  UNAUTHORIZED: 'You are not authorized to perform this action.'
}

const EditRedirect = ({
  deletedRedirect,
  deleteMessage,
  deleteStatus,
  fetchAllRedirects,
  fetchRedirectById,
  redirect,
  redirects,
  removeRedirect,
  resetUpdateRedirectStatus,
  route,
  updateMessage,
  updateRedirect,
  updateStatus
}) => {
  const [isDataLoaded, setIsDataLoaded] = useState()
  const [redirectId, setRedirectId] = useState('')
  const [redirectToEdit, setRedirectToEdit] = useState({})
  const { access_token: accessToken, userDetails: { isContentEditor } } = getCurrentUser()

  useEffect(() => {
    fetchAllRedirects()
  }, [fetchAllRedirects])

  useEffect(() => {
    const redirectId = idx(route, _ => _.match.params.id)
    if (redirectId) {
      setIsDataLoaded(false)
      fetchRedirectById(redirectId)
      setRedirectId(redirectId)
    }
  }, [route, fetchRedirectById])

  console.log('Fetched redirect for editing: ', redirect, redirectToEdit)
  useEffect(() => {
    if (redirect) {
      setRedirectToEdit(redirect)
      setIsDataLoaded(true)
    }
  }, [redirect])

  useEffect(() => {
    if (updateStatus && updateMessage) {
      setIsDataLoaded(true)
    } else if (!updateStatus && updateMessage) {
      setIsDataLoaded(true)
      notification.error({ message: updateMessage })
      resetUpdateRedirectStatus()
    }
  }, [updateStatus, updateMessage, resetUpdateRedirectStatus])

  useEffect(() => {
    if (deleteStatus && deleteMessage) {
      setIsDataLoaded(true)
    } else if (!deleteStatus && deleteMessage) {
      setIsDataLoaded(true)
      notification.error({ message: deleteMessage })
    }
  }, [deleteStatus, deleteMessage, deletedRedirect, route])

  const handleSubmit = ({ id, source, destination, permanent }) => {
    if (!isContentEditor) {
      notification.error({ message: NOTIFICATION_MESSAGES.UNAUTHORIZED })
      return
    }
    // Prevent duplicate redirects
    const duplicate = redirects.find(r => r.source === source && r.destination === destination && r.id !== id)
    if (duplicate) {
      notification.error({ message: 'A redirect with this source and destination already exists.' })
      return
    }

    // Prevent same source and destination
    if (source === destination) {
      notification.error({ message: 'Source and destination cannot be the same.' })
      return
    }

    // Detect redirect chains and optimize
    // Find chain starting from source
    const chain = [source]
    let current = source
    const visited = new Set([source])
    while (true) {
      const nextRedirect = redirects.find(r => r.source === current && r.id !== id)
      if (!nextRedirect || visited.has(nextRedirect.destination)) break
      chain.push(nextRedirect.destination)
      visited.add(nextRedirect.destination)
      current = nextRedirect.destination
    }
    // If chain exists and destination is not already the last, optimize
    if (chain.length > 1 && chain[chain.length - 1] !== destination) {
      // Delete intermediate redirects
      const redirectsToDelete = []
      for (let i = 0; i < chain.length - 1; i++) {
        const r = redirects.find(red => red.source === chain[i] && red.destination === chain[i + 1])
        if (r && r.id !== id) redirectsToDelete.push(r.id)
      }
      // Remove all intermediate redirects
      redirectsToDelete.forEach(rid => removeRedirect(rid, accessToken))
      // Notify user
      notification.info({ message: `Optimized redirect chain from ${chain[0]} to ${chain[chain.length - 1]}. Intermediate redirects removed.` })
    }

    // Update redirect to go directly from source to destination
    console.log('Submitting redirect update with data: ', { id, source, destination, permanent })
    updateRedirect(redirectId, { id, source, destination, permanent }, accessToken)
  }

  const keepEditingRedirect = (
    <Route key="editRedirect" render={({ history }) => (
      <Button
        onClick={() => {
          resetUpdateRedirectStatus()
          history.push(`/redirects/${redirectId}/edit`)
        }}
        type="primary"
      >
        Keep Editing
      </Button>
    )} />
  )

  const goToListRedirectsButton = (
    <Route key="listRedirects" render={({ history }) => (
      <Button
        onClick={() => {
          resetUpdateRedirectStatus()
          history.push('/redirects')
        }}
        type="primary"
      >
        Go to Redirects List
      </Button>
    )} />
  )

  const handleModalClose = () => {
    resetUpdateRedirectStatus()
  }

  const handleDeleteRedirect = () => {
    if (!isContentEditor) {
      notification.error({ message: NOTIFICATION_MESSAGES.UNAUTHORIZED })
      return
    }

    Modal.confirm({
      title: 'Delete Redirect',
      content: 'This redirect will be removed permanently. Are you sure you want to delete this redirect?',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        setIsDataLoaded(false)
        removeRedirect(redirectId, accessToken)
      }
    })
  }

  const updateRedirectButton = <Button className={styles.updateActionButton} onClick={handleSubmit} type='primary'>
    Update
  </Button>

  const deleteRedirectButton = (
    <Button danger onClick={handleDeleteRedirect} type='default'>
      Delete
    </Button>
  )

  return (
    <PageLayout extra={[deleteRedirectButton, updateRedirectButton]} title='Update Redirect'>
      <div className={deleteStatus ? styles.blurred : ''}>
        <Card>
          <RedirectCard
            onSubmit={handleSubmit}
            redirect={redirectToEdit}
            type="edit"
          />
        </Card>
        <Spinner isLoading={!isDataLoaded}/>
      </div>
      {
        (updateStatus === true && updateMessage === 'Redirect updated successfully') &&
        <ResultModal
          actions={[
            goToListRedirectsButton,
            keepEditingRedirect
          ]}
          handleCancel={handleModalClose}
          status="success"
          title={updateMessage}
        />
      }
      {
        (deleteStatus && deleteMessage) &&
        <ResultModal
          actions={[
            goToListRedirectsButton
          ]}
          handleCancel={handleModalClose}
          status="success"
          title={deleteMessage}
        />
      }
    </PageLayout>
  )
}

EditRedirect.propTypes = {
  deleteMessage: PropTypes.string,
  deleteStatus: PropTypes.bool,
  deletedRedirect: PropTypes.object,
  fetchAllRedirects: PropTypes.func,
  fetchRedirectById: PropTypes.array,
  fetchRedirectsList: PropTypes.func,
  redirect: PropTypes.object,
  redirects: PropTypes.array,
  removeRedirect: PropTypes.func,
  resetUpdateRedirectStatus: PropTypes.func,
  route: PropTypes.object.isRequired,
  updateMessage: PropTypes.string,
  updateRedirect: PropTypes.func,
  updateStatus: PropTypes.bool
}

const mapStateToProps = ({ redirects }) => ({
  redirects: redirects.redirects || [],
  redirect: redirects.redirect,
  deleteMessage: redirects.deleteMessage,
  deleteStatus: redirects.deleteStatus,
  updateStatus: redirects.success,
  updateMessage: redirects.message,
  deletedRedirect: redirects.deletedRedirect
})

const mapDispatchToProps = {
  fetchAllRedirects,
  fetchRedirectById,
  fetchRedirectsList,
  removeRedirect: removeRedirectById,
  resetUpdateRedirectStatus,
  updateRedirect
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EditRedirect)
